import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CoverageList } from '@/client/pages/Admin/Media/components/CoverageList';
import { MarketingPageShell } from './components/MarketingPageShell';
import EngagementPage from './EngagementPage';

const TABS = [
  { id: 'coverage', label: 'Coverage' },
  { id: 'mentions', label: 'Mentions' },
  { id: 'replies', label: 'Owned replies' },
] as const;

type TabId = (typeof TABS)[number]['id'];

function parseTab(value: string | null): TabId {
  if (value === 'mentions' || value === 'replies' || value === 'coverage') return value;
  return 'coverage';
}

const OutflowsInboxPage = () => {
  const [params, setParams] = useSearchParams();
  const tab = useMemo(() => parseTab(params.get('tab')), [params]);

  const setTab = (next: TabId) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'coverage') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <MarketingPageShell
      title="Inbox"
      description="Coverage triage, mentions, and owned-post replies in one place."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Radar', href: '/admin/marketing/radar' },
        { label: 'Inbox' },
      ]}
      icon={Inbox}
    >
      <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-white p-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tab === t.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'coverage' && (
        <div>
          <p className="mb-4 text-sm text-gray-600">
            Triage earned and tracked coverage. For owned-post comment replies, switch to{' '}
            <button type="button" className="font-medium text-blue-700 hover:underline" onClick={() => setTab('replies')}>
              Owned replies
            </button>
            .
          </p>
          <CoverageList filters={{ inboxMode: true, defaultStatus: 'new' }} />
        </div>
      )}

      {tab === 'mentions' && (
        <div>
          <p className="mb-4 text-sm text-gray-600">
            Brand and keyword mentions across social. Manage trackers under{' '}
            <Link to="/admin/marketing/sources/listening" className="font-medium text-blue-700 hover:underline">
              Sources → Listening
            </Link>
            .
          </p>
          <CoverageList
            filters={{ channel: 'social', kind: 'mention', inboxMode: true, defaultStatus: 'new' }}
            emptyHint="No social mentions match these filters."
          />
        </div>
      )}

      {tab === 'replies' && <EngagementPage embedded />}
    </MarketingPageShell>
  );
};

export default OutflowsInboxPage;
